import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  DroneInventory,
  DroneAssignment,
  OrganizationDrone,
  OrganizationDronesResponse,
} from '../models/drone.model';

/**
 * Drone Service
 *
 * Handles API communication for drone inventory and assignments in the admin portal.
 * Uses the admin API endpoints for managing drones and assignments.
 */
@Injectable({
  providedIn: 'root',
})
export class DroneService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all drones for an organization (both ADN-assigned and organization-owned)
   * GET /admin/organizations/:orgId/drones
   *
   * Returns both ADN-assigned drones and organization-owned drones
   */
  getOrganizationDrones(organizationId: string): Observable<OrganizationDronesResponse> {
    return this.http
      .get<{ success: boolean; data: OrganizationDronesResponse }>(
        `${this.apiUrl}/admin/organizations/${organizationId}/drones`
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return { adnAssigned: [], organizationOwned: [] };
          }
          return response.data;
        })
      );
  }

  /**
   * Get all drones for an organization as a flat array
   * Combines ADN-assigned and organization-owned drones
   */
  getOrganizationDronesFlat(organizationId: string): Observable<OrganizationDrone[]> {
    return this.getOrganizationDrones(organizationId).pipe(
      map((response) => {
        return [...response.adnAssigned, ...response.organizationOwned];
      })
    );
  }

  /**
   * Get assignment history for an organization
   * GET /admin/organizations/:orgId/drones?includeHistory=true
   */
  getOrganizationDroneHistory(organizationId: string): Observable<DroneAssignment[]> {
    return this.http
      .get<{ success: boolean; data: DroneAssignment[] }>(
        `${this.apiUrl}/admin/organizations/${organizationId}/drones`,
        { params: { includeHistory: 'true' } }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return [];
          }
          return response.data;
        })
      );
  }

  /**
   * Get available drones (not currently assigned)
   * GET /admin/drones?status=available
   */
  getAvailableDrones(): Observable<DroneInventory[]> {
    return this.http
      .get<{ success: boolean; data: DroneInventory[] }>(
        `${this.apiUrl}/admin/drones`,
        { params: { status: 'available' } }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return [];
          }
          return response.data;
        })
      );
  }

  /**
   * Get all drones with optional filters
   * GET /admin/drones
   */
  getAllDrones(filters?: {
    status?: string;
    manufacturerId?: string;
    modelId?: string;
  }): Observable<DroneInventory[]> {
    let params = new HttpParams();
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.manufacturerId) {
      params = params.set('manufacturerId', filters.manufacturerId);
    }
    if (filters?.modelId) {
      params = params.set('modelId', filters.modelId);
    }

    return this.http
      .get<{ success: boolean; data: DroneInventory[] }>(
        `${this.apiUrl}/admin/drones`,
        { params }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return [];
          }
          return response.data;
        })
      );
  }

  /**
   * Get drone by ID with full details
   * GET /admin/drones/:id
   */
  getDroneById(droneId: string): Observable<DroneInventory | null> {
    return this.http
      .get<{ success: boolean; data: DroneInventory }>(
        `${this.apiUrl}/admin/drones/${droneId}`
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return null;
          }
          return response.data;
        })
      );
  }

  /**
   * Assign drone to organization
   * POST /admin/drones/:droneId/assign
   */
  assignDrone(
    droneId: string,
    organizationId: string,
    assignedToProfileId?: string,
    notes?: string
  ): Observable<DroneAssignment> {
    return this.http
      .post<{ success: boolean; data: DroneAssignment }>(
        `${this.apiUrl}/admin/drones/${droneId}/assign`,
        {
          organizationId,
          assignedToProfileId,
          notes,
        }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error('Failed to assign drone');
          }
          return response.data;
        })
      );
  }

  /**
   * Return drone from assignment
   * POST /admin/drones/:droneId/return
   */
  returnDrone(droneId: string, notes?: string): Observable<DroneAssignment> {
    return this.http
      .post<{ success: boolean; data: DroneAssignment }>(
        `${this.apiUrl}/admin/drones/${droneId}/return`,
        { notes }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error('Failed to return drone');
          }
          return response.data;
        })
      );
  }

  /**
   * Revoke drone assignment
   * POST /admin/drones/:droneId/revoke
   */
  revokeAssignment(droneId: string, reason: string): Observable<DroneAssignment> {
    return this.http
      .post<{ success: boolean; data: DroneAssignment }>(
        `${this.apiUrl}/admin/drones/${droneId}/revoke`,
        { reason }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error('Failed to revoke assignment');
          }
          return response.data;
        })
      );
  }

  /**
   * Get assignment history for a drone
   * GET /admin/drones/:droneId/assignments
   */
  getDroneAssignmentHistory(droneId: string): Observable<DroneAssignment[]> {
    return this.http
      .get<{ success: boolean; data: DroneAssignment[] }>(
        `${this.apiUrl}/admin/drones/${droneId}/assignments`
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            return [];
          }
          return response.data;
        })
      );
  }

  /**
   * Get current assignment for a drone
   * GET /admin/drones/:droneId/current-assignment
   */
  getCurrentAssignment(
    droneId: string
  ): Observable<{ assignment: DroneAssignment | null; hasActiveAssignment: boolean }> {
    return this.http
      .get<{
        success: boolean;
        data: DroneAssignment | null;
        hasActiveAssignment: boolean;
      }>(`${this.apiUrl}/admin/drones/${droneId}/current-assignment`)
      .pipe(
        map((response) => {
          if (!response.success) {
            return { assignment: null, hasActiveAssignment: false };
          }
          return {
            assignment: response.data,
            hasActiveAssignment: response.hasActiveAssignment,
          };
        })
      );
  }
}
